import { Pencil } from "lucide-react";

const formatDuration = (mins) => {
  if (mins >= 1440) {
    const d = Math.round(mins / 1440);
    return `${d} day${d > 1 ? "s" : ""}`;
  }
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h} hour${h > 1 ? "s" : ""}`;
  }
  return `${mins} min`;
};

const fmtTimeLabel = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const period = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${period}`;
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2 text-sm">
    <span className="font-medium" style={{ color: "var(--pp-muted)" }}>{label}</span>
    <span className="text-right" style={{ color: "var(--pp-ink)" }} data-testid={testId}>{value || "—"}</span>
  </div>
);

const Section = ({ title, onEdit, children, testId }) => (
  <section
    className="px-4 py-3"
    style={{ background: "var(--pp-cream-soft)", borderRadius: "var(--pp-radius)" }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-1">
      <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--pp-ink)" }}>
        {title}
      </h3>
      <button
        type="button"
        onClick={onEdit}
        className="text-xs font-medium inline-flex items-center gap-1 hover:underline"
        style={{ color: "var(--pp-indigo)" }}
        data-testid={`edit-${testId}`}
      >
        <Pencil className="w-3.5 h-3.5" /> Edit
      </button>
    </div>
    <div>{children}</div>
  </section>
);

export default function Step4Review({ services, selectedServiceIds, date, time, details, onJumpTo }) {
  const chosen = services.filter((s) => selectedServiceIds.includes(s.service_id));
  const totalDuration = chosen.reduce((acc, s) => acc + s.duration, 0);
  const dateLabel = date
    ? date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "—";

  return (
    <div className="pp-step-fade flex flex-col gap-2.5" data-testid="step-4-review">
      <h2 className="text-xl md:text-2xl font-medium mb-1">Review your booking</h2>
      <p className="text-xs sm:text-sm mb-2" style={{ color: "var(--pp-muted)" }}>
        Almost done. Check everything looks right before confirming.
      </p>

      <Section title="Services" onEdit={() => onJumpTo(1)} testId="review-services">
        <ul className="text-sm flex flex-col gap-1 mt-1" data-testid="review-services-list">
          {chosen.map((s) => (
            <li key={s.service_id} className="flex justify-between">
              <span style={{ color: "var(--pp-ink)" }}>{s.name}</span>
              <span style={{ color: "var(--pp-muted)" }}>{formatDuration(s.duration)}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between mt-2 pt-2 text-sm font-semibold" style={{ borderTop: "1px solid var(--pp-border)" }}>
          <span>Total</span>
          <span data-testid="review-total-duration">{formatDuration(totalDuration)}</span>
        </div>
      </Section>

      <Section title="Date & Time" onEdit={() => onJumpTo(2)} testId="review-datetime">
        <Row label="Date" value={dateLabel} testId="review-date" />
        <Row label="Time" value={fmtTimeLabel(time)} testId="review-time" />
      </Section>

      <Section title="Owner" onEdit={() => onJumpTo(3)} testId="review-owner">
        <Row label="Name" value={`${details.owner_first_name} ${details.owner_last_name}`} testId="review-owner-name" />
        <Row label="Phone" value={details.owner_phone} testId="review-owner-phone" />
        <Row label="Email" value={details.owner_email} testId="review-owner-email" />
      </Section>

      <Section title="Pet" onEdit={() => onJumpTo(3)} testId="review-pet">
        <Row label="Name" value={details.pet_name} testId="review-pet-name" />
        <Row label="Breed" value={details.pet_breed} testId="review-pet-breed" />
        <Row label="Notes" value={details.pet_notes} testId="review-pet-notes" />
      </Section>
    </div>
  );
}
